import { useState, useMemo } from 'react'
import {
  Box,
  Button,
  Flex,
  Input,
  Text,
  Badge,
  VStack,
  HStack,
  DialogRoot,
  DialogBackdrop,
  DialogPositioner,
  DialogContent,
  DialogCloseTrigger,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
  Collapsible,
  Switch,
  Checkbox,
} from '@chakra-ui/react'
import { ChevronRight, ChevronDown, Search, BookOpen, Plus, Pencil } from 'lucide-react'
import { useChartAccounts, useResidenceChartAccounts, useToggleResidenceChartAccount, useUpdateResidenceChartAccount, useCreateCustomChartAccount, useActivateChartAccount, useBulkToggleResidenceChartAccounts } from '../hooks'
import { useAuth } from '@/features/auth/hooks/useAuth'
import { accountingToaster as toaster } from '../pages/AccountingLayout'
import type { ChartAccount, ResidenceChartAccount } from '../types'

interface ChartAccountsManageModalProps {
  isOpen: boolean
  onClose: () => void
  residenceId: string
}

interface AccountTreeNode extends ChartAccount {
  children: AccountTreeNode[]
  level: number
  residenceAccount?: ResidenceChartAccount
}

const ACCOUNT_CLASSES = [
  { code: '1', name: 'Classe 1 - Capitaux', color: 'orange' },
  { code: '4', name: 'Classe 4 - Tiers', color: 'blue' },
  { code: '5', name: 'Classe 5 - Financiers', color: 'green' },
  { code: '6', name: 'Classe 6 - Charges', color: 'red' },
  { code: '7', name: 'Classe 7 - Produits', color: 'purple' },
]

export function ChartAccountsManageModal({
  isOpen,
  onClose,
  residenceId,
}: ChartAccountsManageModalProps) {
  const { data: authData } = useAuth()
  const { data: allAccounts, isLoading: loadingAll } = useChartAccounts()
  const { data: residenceAccounts, isLoading: loadingResidence } = useResidenceChartAccounts(residenceId)
  const toggleAccount = useToggleResidenceChartAccount()
  const activateAccount = useActivateChartAccount()
  const bulkToggle = useBulkToggleResidenceChartAccounts()
  const updateAccount = useUpdateResidenceChartAccount()
  const createCustomAccount = useCreateCustomChartAccount()
  // LOT 2 - deleteCustomAccount sera utilisé plus tard

  const [searchTerm, setSearchTerm] = useState('')
  const [expandedClasses, setExpandedClasses] = useState<string[]>([])
  const [editingLabel, setEditingLabel] = useState<string | null>(null)
  const [customLabel, setCustomLabel] = useState('')

  // État pour création de sous-compte inline
  const [addingSubAccountTo, setAddingSubAccountTo] = useState<string | null>(null)
  const [subAccountCode, setSubAccountCode] = useState('')
  const [subAccountName, setSubAccountName] = useState('')
  const [subAccountDeployToAgency, setSubAccountDeployToAgency] = useState(false)

  // Construire l'arbre avec les comptes résidence
  const accountTree = useMemo(() => {
    if (!allAccounts) return []

    const residenceMap = new Map<string, ResidenceChartAccount>()
    residenceAccounts?.forEach((ra) => {
      if (ra.chart_account) {
        residenceMap.set(ra.chart_account.id, ra)
      }
    })

    // Filtrer par recherche
    let filteredAccounts = allAccounts
    if (searchTerm) {
      const term = searchTerm.toLowerCase()
      filteredAccounts = allAccounts.filter(
        (a) =>
          a.code.toLowerCase().includes(term) ||
          a.name.toLowerCase().includes(term)
      )
    }

    // Grouper par classe
    return ACCOUNT_CLASSES.map((cls) => {
      const classAccounts = filteredAccounts.filter((a) => a.code.startsWith(cls.code))
      return {
        ...cls,
        accounts: buildTree(classAccounts, residenceMap),
        count: classAccounts.length,
        activeCount: classAccounts.filter((a) => {
          const ra = residenceMap.get(a.id)
          return ra?.is_active
        }).length,
      }
    })
  }, [allAccounts, residenceAccounts, searchTerm])

  function buildTree(
    accounts: ChartAccount[],
    residenceMap: Map<string, ResidenceChartAccount>
  ): AccountTreeNode[] {
    const sorted = [...accounts].sort((a, b) => a.code.localeCompare(b.code))
    const nodes: AccountTreeNode[] = []

    sorted.forEach((account) => {
      nodes.push({
        ...account,
        children: [],
        level: account.code.length,
        residenceAccount: residenceMap.get(account.id),
      })
    })

    return nodes
  }

  // LOT 2 - handleToggleAccount sera utilisé plus tard

  const handleSaveCustomLabel = async (residenceAccountId: string) => {
    try {
      await updateAccount.mutateAsync({
        id: residenceAccountId,
        custom_label: customLabel || null,
      })
      toaster.create({
        title: 'Succès',
        description: 'Libellé personnalisé enregistré',
        type: 'success',
      })
      setEditingLabel(null)
      setCustomLabel('')
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Erreur lors de la mise à jour',
        type: 'error',
      })
    }
  }

  const toggleClass = (code: string) => {
    // Un seul accordéon ouvert à la fois
    setExpandedClasses((prev) =>
      prev.includes(code) ? [] : [code]
    )
  }

  // LOT 2 - handleDeleteCustomAccount sera utilisé plus tard

  // Activer/désactiver un compte (avec création récursive si nécessaire)
  const handleToggleWithCreate = async (account: AccountTreeNode, newState: boolean) => {
    try {
      // Trouver tous les comptes de la hiérarchie (parent + enfants)
      const allRelatedAccounts = allAccounts?.filter((a) =>
        a.code === account.code || a.code.startsWith(account.code)
      ) || []

      if (newState) {
        // Activation : créer les comptes qui n'existent pas encore
        for (const acc of allRelatedAccounts) {
          const existingRa = residenceAccounts?.find((ra) => ra.chart_account_id === acc.id)
          if (!existingRa) {
            await activateAccount.mutateAsync({ residenceId, chartAccountId: acc.id })
          } else if (!existingRa.is_active) {
            await toggleAccount.mutateAsync({ id: existingRa.id, isActive: true })
          }
        }
      } else {
        // Désactivation : toggle tous les comptes existants
        const existingIds = allRelatedAccounts
          .map((acc) => residenceAccounts?.find((ra) => ra.chart_account_id === acc.id)?.id)
          .filter((id): id is string => !!id)

        if (existingIds.length > 0) {
          await bulkToggle.mutateAsync({ ids: existingIds, isActive: false })
        }
      }

      toaster.create({ title: 'Succès', description: newState ? 'Compte(s) activé(s)' : 'Compte(s) désactivé(s)', type: 'success' })
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Erreur lors de la mise à jour',
        type: 'error',
      })
    }
  }

  // Suggérer le prochain code disponible pour un sous-compte
  const suggestNextCode = (parentCode: string): string => {
    const childCodes = allAccounts
      ?.filter((a) => a.code.startsWith(parentCode) && a.code.length === parentCode.length + 1)
      .map((a) => a.code) || []

    if (childCodes.length === 0) {
      return parentCode + '1'
    }

    // Trouver le max et incrémenter
    const maxSuffix = Math.max(...childCodes.map((c) => parseInt(c.slice(-1)) || 0))
    return parentCode + (maxSuffix + 1)
  }

  // Créer un sous-compte
  const handleCreateSubAccount = async (parentCode: string) => {
    if (!subAccountCode.trim() || !subAccountName.trim()) {
      toaster.create({ title: 'Erreur', description: 'Le code et le libellé sont obligatoires', type: 'error' })
      return
    }

    // Valider que le code commence par le parent
    if (!subAccountCode.startsWith(parentCode)) {
      toaster.create({ title: 'Erreur', description: `Le code doit commencer par ${parentCode}`, type: 'error' })
      return
    }

    try {
      await createCustomAccount.mutateAsync({
        residenceId,
        code: subAccountCode,
        name: subAccountName,
        deployToAgency: subAccountDeployToAgency,
        agencyId: authData?.profile?.agency_id ?? undefined,
      })
      toaster.create({
        title: 'Succès',
        description: subAccountDeployToAgency ? 'Sous-compte créé et déployé sur le PCG de l\'agence' : 'Sous-compte créé',
        type: 'success',
      })
      setAddingSubAccountTo(null)
      setSubAccountCode('')
      setSubAccountName('')
      setSubAccountDeployToAgency(true)
    } catch (error) {
      toaster.create({
        title: 'Erreur',
        description: error instanceof Error ? error.message : 'Erreur lors de la création',
        type: 'error',
      })
    }
  }

  // LOT 2 - getClassColor sera utilisé plus tard

  const isLoading = loadingAll || loadingResidence
  const isPending = toggleAccount.isPending || activateAccount.isPending || bulkToggle.isPending || updateAccount.isPending || createCustomAccount.isPending

  return (
    <DialogRoot open={isOpen} onOpenChange={(e) => !e.open && onClose()} size="xl">
      <DialogBackdrop />
      <DialogPositioner>
        <DialogContent maxW="900px" maxH="85vh">
          <DialogCloseTrigger />
          <DialogHeader>
            <DialogTitle>
              <Flex align="center" gap={2}>
                <BookOpen size={20} />
                Gérer le plan comptable
              </Flex>
            </DialogTitle>
          </DialogHeader>
          <DialogBody overflowY="auto">
            {/* Barre de recherche */}
            <Box mb={4}>
              <Flex align="center" gap={2} bg="gray.50" borderRadius="md" px={3} py={2}>
                <Search size={18} color="gray" />
                <Input
                  placeholder="Rechercher par code ou libellé..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  variant="outline"
                  size="sm"
                  border="none"
                />
              </Flex>
            </Box>

            {isLoading ? (
              <Text>Chargement...</Text>
            ) : (
              <VStack align="stretch" gap={2}>
                {accountTree.map((cls) => (
                  <Box key={cls.code} borderWidth="1px" borderRadius="md" overflow="hidden">
                    {/* Header de classe */}
                    <Flex
                      align="center"
                      justify="space-between"
                      p={3}
                      bg={`${cls.color}.50`}
                      cursor="pointer"
                      onClick={() => toggleClass(cls.code)}
                      _hover={{ bg: `${cls.color}.100` }}
                    >
                      <HStack>
                        {expandedClasses.includes(cls.code) ? (
                          <ChevronDown size={18} />
                        ) : (
                          <ChevronRight size={18} />
                        )}
                        <Text fontWeight="semibold" color={`${cls.color}.700`}>
                          {cls.name}
                        </Text>
                      </HStack>
                      <HStack gap={2}>
                        <Badge colorPalette={cls.color} variant="subtle">
                          {cls.activeCount} / {cls.count} actifs
                        </Badge>
                      </HStack>
                    </Flex>

                    {/* Liste des comptes */}
                    <Collapsible.Root open={expandedClasses.includes(cls.code)}>
                      <Collapsible.Content>
                        <Box maxH="300px" overflowY="auto">
                          {cls.accounts.length === 0 ? (
                            <Box p={4} textAlign="center" color="gray.500">
                              <Text fontSize="sm">Aucun compte trouvé</Text>
                            </Box>
                          ) : (
                            cls.accounts.map((account) => (
                              <AccountRow
                                key={account.id}
                                account={account}
                                classColor={cls.color}
                                onToggle={handleToggleWithCreate}
                                editingLabel={editingLabel}
                                setEditingLabel={setEditingLabel}
                                customLabel={customLabel}
                                setCustomLabel={setCustomLabel}
                                onSaveLabel={handleSaveCustomLabel}
                                isPending={isPending}
                                addingSubAccountTo={addingSubAccountTo}
                                setAddingSubAccountTo={setAddingSubAccountTo}
                                subAccountCode={subAccountCode}
                                setSubAccountCode={setSubAccountCode}
                                subAccountName={subAccountName}
                                setSubAccountName={setSubAccountName}
                                subAccountDeployToAgency={subAccountDeployToAgency}
                                setSubAccountDeployToAgency={setSubAccountDeployToAgency}
                                onCreateSubAccount={handleCreateSubAccount}
                                suggestNextCode={suggestNextCode}
                              />
                            ))
                          )}
                        </Box>
                      </Collapsible.Content>
                    </Collapsible.Root>
                  </Box>
                ))}
              </VStack>
            )}
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={onClose}>
              Fermer
            </Button>
          </DialogFooter>
        </DialogContent>
      </DialogPositioner>
    </DialogRoot>
  )
}

interface AccountRowProps {
  account: AccountTreeNode
  classColor: string
  onToggle: (account: AccountTreeNode, newState: boolean) => void
  editingLabel: string | null
  setEditingLabel: (id: string | null) => void
  customLabel: string
  setCustomLabel: (label: string) => void
  onSaveLabel: (id: string) => void
  isPending: boolean
  addingSubAccountTo: string | null
  setAddingSubAccountTo: (code: string | null) => void
  subAccountCode: string
  setSubAccountCode: (code: string) => void
  subAccountName: string
  setSubAccountName: (name: string) => void
  subAccountDeployToAgency: boolean
  setSubAccountDeployToAgency: (deploy: boolean) => void
  onCreateSubAccount: (parentCode: string) => void
  suggestNextCode: (parentCode: string) => string
}

function AccountRow({
  account,
  classColor,
  onToggle,
  editingLabel,
  setEditingLabel,
  customLabel,
  setCustomLabel,
  onSaveLabel,
  isPending,
  addingSubAccountTo,
  setAddingSubAccountTo,
  subAccountCode,
  setSubAccountCode,
  subAccountName,
  setSubAccountName,
  subAccountDeployToAgency,
  setSubAccountDeployToAgency,
  onCreateSubAccount,
  suggestNextCode,
}: AccountRowProps) {
  const ra = account.residenceAccount
  const isEditing = editingLabel === ra?.id
  const isAddingSubAccount = addingSubAccountTo === account.code
  const indent = (account.level - 1) * 16
  const isActive = ra?.is_active ?? false

  return (
    <>
      <Flex
        align="center"
        justify="space-between"
        px={4}
        py={2}
        borderBottomWidth="1px"
        borderColor="gray.100"
        _hover={{ bg: 'gray.50' }}
        pl={`${16 + indent}px`}
      >
        <HStack flex={1} gap={3}>
          <Badge variant="outline" fontFamily="mono" fontSize="xs" colorPalette={classColor}>
            {account.code}
          </Badge>
          {isEditing ? (
            <HStack flex={1}>
              <Input
                size="sm"
                value={customLabel}
                onChange={(e) => setCustomLabel(e.target.value)}
                placeholder={account.name}
                autoFocus
                css={{
                  '&:focus, &:focus-visible': {
                    borderColor: `var(--chakra-colors-${classColor}-500) !important`,
                    boxShadow: `0 0 0 1px var(--chakra-colors-${classColor}-500) !important`,
                    outline: 'none',
                  },
                }}
              />
              <Button
                size="xs"
                colorPalette={classColor}
                onClick={() => onSaveLabel(ra!.id)}
                loading={isPending}
              >
                OK
              </Button>
              <Button
                size="xs"
                variant="ghost"
                colorPalette={classColor}
                onClick={() => {
                  setEditingLabel(null)
                  setCustomLabel('')
                }}
              >
                Annuler
              </Button>
            </HStack>
          ) : (
            <HStack flex={1} gap={2}>
              <Text
                fontSize="sm"
                color={isActive ? 'gray.800' : 'gray.400'}
                textDecoration={isActive ? 'none' : 'line-through'}
              >
                {ra?.custom_label || account.name}
                {ra?.custom_label && (
                  <Text as="span" fontSize="xs" color="gray.400" ml={2}>
                    ({account.name})
                  </Text>
                )}
              </Text>
              {ra && (
                <Button
                  size="xs"
                  variant="ghost"
                  colorPalette={classColor}
                  onClick={() => {
                    setEditingLabel(ra.id)
                    setCustomLabel(ra.custom_label || account.name)
                  }}
                  title="Modifier le libellé"
                  px={1}
                  minW="auto"
                >
                  <Pencil size={12} />
                </Button>
              )}
            </HStack>
          )}
        </HStack>

        <HStack gap={2}>
          {/* Bouton ajouter sous-compte */}
          <Button
            size="xs"
            variant="ghost"
            colorPalette={classColor}
            onClick={() => {
              setAddingSubAccountTo(account.code)
              setSubAccountCode(suggestNextCode(account.code))
              setSubAccountName('')
              setSubAccountDeployToAgency(false)
            }}
            title="Ajouter un sous-compte"
            px={1}
          >
            <Plus size={14} />
          </Button>

          {/* Switch activation */}
          <Switch.Root
            checked={isActive}
            onCheckedChange={(e) => onToggle(account, !!e.checked)}
            disabled={isPending}
            size="sm"
            colorPalette={classColor}
          >
            <Switch.HiddenInput />
            <Switch.Control>
              <Switch.Thumb />
            </Switch.Control>
          </Switch.Root>
        </HStack>
      </Flex>

      {/* Formulaire inline pour ajouter sous-compte */}
      {isAddingSubAccount && (
        <Flex
          align="center"
          gap={2}
          px={4}
          py={2}
          bg={`${classColor}.50`}
          borderBottomWidth="1px"
          borderColor={`${classColor}.200`}
          pl={`${32 + indent}px`}
        >
          <Input
            size="xs"
            w="80px"
            value={subAccountCode}
            onChange={(e) => setSubAccountCode(e.target.value)}
            placeholder="Code"
            fontFamily="mono"
          />
          <Input
            size="xs"
            flex={1}
            value={subAccountName}
            onChange={(e) => setSubAccountName(e.target.value)}
            placeholder="Libellé du sous-compte"
          />
          <Checkbox.Root
            checked={subAccountDeployToAgency}
            onCheckedChange={(e) => setSubAccountDeployToAgency(!!e.checked)}
            size="sm"
          >
            <Checkbox.HiddenInput />
            <Checkbox.Control />
            <Checkbox.Label fontSize="xs" whiteSpace="nowrap">PCG agence</Checkbox.Label>
          </Checkbox.Root>
          <Button
            size="xs"
            colorPalette={classColor}
            onClick={() => onCreateSubAccount(account.code)}
            loading={isPending}
          >
            Créer
          </Button>
          <Button
            size="xs"
            variant="ghost"
            onClick={() => {
              setAddingSubAccountTo(null)
              setSubAccountCode('')
              setSubAccountName('')
            }}
          >
            Annuler
          </Button>
        </Flex>
      )}
    </>
  )
}
